import { Component, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";

/** @static base class name */
const baseClass = "ux-dot-pagination";

@Component({
    tag: "ux-dot-pagination",
    styleUrl: "dot-pagination.scss",
    shadow: true
})
export class DotPagination {
    /**
     * Define total number of pages
     */
    @Prop() readonly total: number = 0;

    /**
     * Define current page index
     */
    @Prop() readonly page: number = 1;

    /**
     * Define maximum number of visible dots
     */
    @Prop() readonly maxDots: number = 7;

    /**
     * Define visibility of navigation arrows
     * @default false - visible
     */
    @Prop() readonly withoutArrows: boolean = false;

    /**
     * Define navigation item loop
     * If clicking an arrow on the last page should jump to the first page and vice versa
     * @default false - visible
     */
    @Prop() readonly loop: boolean = false;

    /**
     * Define container aria-label
     */
    @Prop() readonly label: string = "";

    /**
     * Define next button aria-label
     */
    @Prop() readonly ariaLabelNext: string = "";

    /**
     * Define prev button aria-label
     */
    @Prop() readonly ariaLabelPrev: string = "";

    /**
     * Define dot item label
     * Will be extended with dot index`
     * @Sample 'Menu Item' shows as 'Menu Item 5'
     */
    @Prop() readonly ariaLabelMenuItem: string = "";

    /** The current index */
    @State() current: number = this.page - 1;
    /** Storage for the page indexes */
    @State() items: number[] = [];

    @Watch("total")
    setTotal() {
        const { total } = this;
        this.items =
            total > 0
                ? Array(total)
                      .fill(1)
                      .map((_item, index) => index)
                : [];
    }

    @Watch("page")
    setPage() {
        this.setCurrent(this.page - 1);
    }

    /** Emitted when a dot or arrow was clicked `<{ page: number; }>` */
    @Event({ eventName: "uxPaginationChange", cancelable: true, bubbles: true })
    paginationChange!: EventEmitter<{
        page: number;
    }>;

    private setCurrent = (index: number): void => {
        this.current = Math.max(-1, Math.min(this.total - 1, index));
    };

    private handleClickDotAt = (index: number) => {
        if (index === this.current) {
            return;
        }
        const event = this.paginationChange.emit({
            page: index + 1
        });
        if (event.defaultPrevented) {
            return;
        }
        this.setCurrent(index);
    };

    private handlePrevPage = () => {
        if (!this.loop && this.current <= 0) {
            return;
        }
        const index = this.current > 0 ? this.current - 1 : this.total - 1;
        this.handleClickDotAt(index);
    };

    private handleNextPage = () => {
        if (!this.loop && this.current >= this.total - 1) {
            return;
        }
        const index = this.current < this.total - 1 ? this.current + 1 : 0;
        this.handleClickDotAt(index);
    };

    componentWillLoad() {
        this.setTotal();
    }

    render() {
        const { current, items, total, maxDots, withoutArrows, loop, handleClickDotAt, handlePrevPage, handleNextPage } = this;
        const limit = Math.max(3, maxDots);
        const activeDot = Math.max(0, Math.min(current, total - 1));
        const startIndex = total > limit ? Math.min(total - limit, Math.max(0, Math.round(current - 0.5 * (limit - 1)))) : 0;
        const endIndex = startIndex + Math.min(total, limit);
        return (
            <Host
                class={{
                    [baseClass]: true
                }}
            >
                <div class={`${baseClass}__container`} role={"menu"} aria-label={this.label}>
                    {!withoutArrows && (
                        <ux-icon
                            name="chevron-right"
                            weight="normal"
                            class={{
                                [`${baseClass}__arrow`]: true,
                                [`${baseClass}__arrow--prev`]: true,
                                [`${baseClass}__arrow--disabled`]: !loop && current === 0
                            }}
                            onClick={handlePrevPage}
                            tabindex={0}
                            onKeyPress={(e: KeyboardEvent) => {
                                if (e.code === "Enter" || e.code === "Space") {
                                    this.handlePrevPage();
                                }
                            }}
                            role={"button"}
                            aria-disabled={!loop && current === 0 ? "true" : "false"}
                            aria-label={this.ariaLabelNext}
                        />
                    )}
                    <div class={`${baseClass}__inner`}>
                        {items.map((listIndex) => (
                            <span
                                role="menuitem"
                                aria-label={`${this.ariaLabelMenuItem} ${listIndex + 1}`}
                                class={{
                                    [`${baseClass}__item`]: true,
                                    [`${baseClass}__item--active`]: listIndex === activeDot,
                                    [`${baseClass}__item--hidden`]: listIndex < startIndex || listIndex >= endIndex,
                                    [`${baseClass}__item--small`]:
                                        (listIndex === startIndex && listIndex > 0) ||
                                        (listIndex === endIndex - 1 && listIndex < total - 1)
                                }}
                                tabindex={0}
                                onClick={() => handleClickDotAt(listIndex)}
                                onKeyPress={(e: KeyboardEvent) => {
                                    if (e.code === "Enter" || e.code === "Space") {
                                        this.handleClickDotAt(listIndex);
                                    }
                                }}
                            >
                                <span class={`${baseClass}__dot`} />
                            </span>
                        ))}
                    </div>
                    {!withoutArrows && (
                        <ux-icon
                            name="chevron-right"
                            weight="normal"
                            class={{
                                [`${baseClass}__arrow`]: true,
                                [`${baseClass}__arrow--next`]: true,
                                [`${baseClass}__arrow--disabled`]: !loop && current === total - 1
                            }}
                            onKeyPress={(e: KeyboardEvent) => {
                                if (e.code === "Enter" || e.code === "Space") {
                                    this.handleNextPage();
                                }
                            }}
                            tabindex={0}
                            aria-label={this.ariaLabelNext}
                            aria-disabled={!loop && current === total - 1 ? "true" : "false"}
                            role={"button"}
                            onClick={handleNextPage}
                        />
                    )}
                </div>
            </Host>
        );
    }
}
